// LF360-2047: editing a role — the rules the form follows before the server
// checks them again (lf360-be lib/permissions/roleRules.js).
import moment from "moment";

export const NAME_MIN = 2;
export const NAME_MAX = 50;
export const DESCRIPTION_MAX = 500;
const RESERVED_NAMES = ["system admin", "organization admin"];

// Confirmations look and stack like the shared DeleteModal: above the card and
// whatever menu it opened from.
export const CONFIRM_MODAL = { className: "plan-page-action-modal", zIndex: 2000 };

const byKey = (catalog) => new Map((catalog?.permissions || []).map((p) => [p.key, p]));
const inCatalogOrder = (catalog, keys) => {
  const set = new Set(keys);
  return (catalog?.permissions || []).map((p) => p.key).filter((key) => set.has(key));
};

/**
 * `keys` with `key` switched on or off. On also turns on what it requires; off
 * also turns off whatever depends on it (draft, story 4).
 */
export function toggle(catalog, keys, key, on) {
  const perms = byKey(catalog);
  const out = new Set(keys);

  if (on) {
    const add = (k) => {
      if (out.has(k) || !perms.has(k)) return;
      out.add(k);
      perms.get(k).requires.forEach(add);
    };
    add(key);
  } else {
    out.delete(key);
    let changed = true;
    while (changed) {
      changed = false;
      for (const k of out) {
        if (perms.get(k)?.requires.some((req) => !out.has(req))) {
          out.delete(k);
          changed = true;
        }
      }
    }
  }

  return inCatalogOrder(catalog, [...out]);
}

/** Labels of the permissions added and removed between `before` and `after`. */
export function permissionDiff(catalog, before, after) {
  const perms = byKey(catalog);
  const label = (key) => perms.get(key)?.label || key;
  const had = new Set(before);
  const has = new Set(after);
  return {
    added: after.filter((key) => !had.has(key)).map(label),
    removed: before.filter((key) => !has.has(key)).map(label),
  };
}

/** Labels of the sensitive permissions among `keys`. */
export const sensitiveIn = (catalog, keys) => {
  const perms = byKey(catalog);
  return keys.filter((key) => perms.get(key)?.sensitive).map((key) => perms.get(key).label);
};

/** The form's fields that differ from `role`, as the server takes them. */
export function changesOf(role, draft) {
  const out = {};
  if (draft.name.trim() !== role.name) out.name = draft.name.trim();
  if (draft.description.trim() !== (role.description || "")) out.description = draft.description.trim();
  if (draft.siteScoping !== role.siteScoping) out.siteScoping = draft.siteScoping;
  const same = draft.permissions.length === role.permissions.length
    && draft.permissions.every((key, i) => key === role.permissions[i]);
  if (!same) out.permissions = draft.permissions;
  return out;
}

/** What is wrong with the form, in the draft's words; null when nothing. */
export function validationError(draft) {
  const name = draft.name.trim();
  if (!name || !draft.permissions.length) return "Please fill out required fields";
  if (name.length < NAME_MIN || name.length > NAME_MAX) {
    return `Name must be ${NAME_MIN} to ${NAME_MAX} characters`;
  }
  if (RESERVED_NAMES.includes(name.toLowerCase())) return "This name is reserved";
  if (draft.description.trim().length > DESCRIPTION_MAX) {
    return `Description must be at most ${DESCRIPTION_MAX} characters`;
  }
  return null;
}

/**
 * What is wrong with each field, to mark it under the toast above: the toast
 * is the draft's, and alone it does not say which field to fix.
 */
export function fieldErrors(draft) {
  const errors = {};
  const name = draft.name.trim();
  if (!name) errors.name = "Enter a role name";
  else if (name.length < NAME_MIN || name.length > NAME_MAX) {
    errors.name = `Name must be ${NAME_MIN} to ${NAME_MAX} characters`;
  } else if (RESERVED_NAMES.includes(name.toLowerCase())) errors.name = "This name is reserved";
  if (draft.description.trim().length > DESCRIPTION_MAX) {
    errors.description = `Description must be at most ${DESCRIPTION_MAX} characters`;
  }
  if (!draft.permissions.length) errors.permissions = "Turn on at least one permission";
  return errors;
}

/** A time as the roles screen shows it. */
export const utc = (date) => `${moment(date).utc().format("DD MMM YYYY, HH:mm")}, UTC`;

/** The draft's version-conflict toast. */
export const conflictMessage = ({ updatedBy, updatedAt }, formatTime = utc) =>
  `This role was changed by ${updatedBy || "another user"} at ${formatTime(updatedAt)}. `
  + "Reload to see the latest version.";

/** The message of a failed role request: the conflict toast, or the server's. */
export const failureMessage = (error, fallback) => {
  const body = error?.response?.data?.error;
  if (error?.response?.status === 409) return conflictMessage(body || {});
  return body?.message || fallback;
};
